import React from 'react'

import {
  TQCombinationMeals,
  TQCombinationMealsVariables,
  TQProductList,
  useQCombinationMeals,
} from '~graphql/generated/clientGraphql'

type TProductCategories = TQProductList['productCategories']
type TCategory = TProductCategories[number]
type TProduct = TCategory['products'][number]
type TCombinationMealProducts = TQCombinationMeals['recipes']

type TOptions = {
  variables: TQCombinationMealsVariables
}

/**
 *  Returns replacePrices function
 *  If product is combination meal, it will replace price with combinationMealPrice
 *
 *  If data is not loaded yet, this hook returns `null`.
 *  You can use it as a condition in the place where you are replacing prices.
 */
export const useReplacePrice = ({ variables }: TOptions) => {
  const { data: combinationMealsData } = useQCombinationMeals({
    variables,
  })

  const replacePrices = React.useCallback(
    (category: TCategory) => {
      if (!combinationMealsData) return category

      const combinationMealProducts = combinationMealsData.recipes
      const replaceProductPrice = getReplaceProductPrice(combinationMealProducts)

      return {
        ...category,
        products: category.products.map(replaceProductPrice),
      }
    },
    [combinationMealsData],
  )

  return combinationMealsData ? replacePrices : null
}

// * HELPERS

// replace price of combination meal products
// curried function that takes combinationMealProduct as the 1st argument and a product as the 2nd argument
// therefore it can be used to create a callback for Array.map
const getReplaceProductPrice =
  (combinationMealProducts: TCombinationMealProducts) => (product: TProduct) => {
    const { isCombinationMeal, id } = product

    if (!isCombinationMeal) return product

    const combinationMealProduct = combinationMealProducts.find(
      (combinationMeal) => combinationMeal.id === id,
    )
    const combinationMealPrice = combinationMealProduct?.combinationMealPrice

    return {
      ...product,
      price: combinationMealPrice ?? product.price,
    }
  }
